import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const THEME_STORAGE_KEY = 'radiologox-theme';

export type Theme = 'dark' | 'light';

const THEME_COLORS: Record<Theme, string> = {
  dark: '#05080a',
  light: '#e8e4df',
};

const IOS_STATUS_BAR_STYLES: Record<Theme, string> = {
  dark: 'black-translucent',
  light: 'default',
};

function readStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    /* private mode / blocked storage */
  }
  return null;
}

function persistTheme(theme: Theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

function applyThemeToDocument(newTheme: Theme) {
  const root = document.documentElement;
  root.classList.remove('theme-light', 'theme-dark', 'dark', 'light');

  if (newTheme === 'light') {
    root.classList.add('theme-light', 'light');
  } else {
    root.classList.add('theme-dark', 'dark');
  }

  // Keep in sync with index.html early-paint inline styles. Those win over
  // `.theme-light { --color-app-bg }` until refresh if we only flip classes.
  const themeColor = THEME_COLORS[newTheme];
  root.style.backgroundColor = themeColor;
  root.style.colorScheme = newTheme;
  root.style.setProperty('--color-app-bg', themeColor);

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', themeColor);
  document
    .querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
    ?.setAttribute('content', IOS_STATUS_BAR_STYLES[newTheme]);
}

/** Aplica tema guardado o dark por defecto (sin escribir en localStorage). */
export function initializeThemeFromStorage(): Theme {
  const theme = readStoredTheme() ?? 'dark';
  applyThemeToDocument(theme);
  return theme;
}

export const useThemeStore = defineStore('theme', () => {
  const theme = ref<Theme>(readStoredTheme() ?? 'dark');
  const isTransitioning = ref(false);

  function applyTheme(newTheme: Theme, options?: { persist?: boolean }) {
    theme.value = newTheme;
    applyThemeToDocument(newTheme);
    if (options?.persist) {
      persistTheme(newTheme);
    }
  }

  function toggleTheme() {
    isTransitioning.value = true;
    const newTheme: Theme = theme.value === 'dark' ? 'light' : 'dark';
    applyTheme(newTheme, { persist: true });

    setTimeout(() => {
      isTransitioning.value = false;
    }, 300);
  }

  function initialize() {
    applyTheme(readStoredTheme() ?? 'dark');
  }

  return {
    theme,
    isTransitioning,
    isDark: computed(() => theme.value === 'dark'),
    isLight: computed(() => theme.value === 'light'),
    toggleTheme,
    initialize,
    applyTheme,
  };
});
